import { Polygon } from 'fabric';
import {
  calculateDistance,
  isNearPoint,
  snapValue,
  getObjectEndpoints,
  snapToNearestEndpoint,
  applyHierarchicalSnap,
  ENDPOINT_SNAP_DISTANCE,
} from './snapUtils';
import { GARDEN_OBJECT_TYPES, createGardenObject } from '../../../constants/gardenObjectTypes';
import { getObjectTypeById } from '../../../config';

/**
 * PolygonTool - Handles polygon drawing logic for garden outline
 *
 * Features:
 * - Click-to-add-vertex workflow (multi-click, not drag)
 * - Snap-to-grid support for vertices (via snapUtils)
 * - Snap-to-endpoint support for vertices (via snapUtils)
 * - Auto-close when clicking near first vertex (10px tolerance)
 * - Enter key or double-click to close polygon
 * - Minimum 3 vertices required
 * - Visual feedback: vertex dots, connecting lines, live preview
 *
 * Design based on: Issue #11 and docs/plans/PLAN_INITIAL_SETUP_WIZARD.md
 */

// Re-export snap utilities for backward compatibility with existing code
export {
  calculateDistance,
  isNearPoint,
  snapValue,
  getObjectEndpoints,
  snapToNearestEndpoint,
  applyHierarchicalSnap,
  ENDPOINT_SNAP_DISTANCE,
};

/**
 * Calculate perpendicular distance from point to line segment
 *
 * @param {object} point - Point {x, y}
 * @param {object} lineStart - Line segment start {x, y}
 * @param {object} lineEnd - Line segment end {x, y}
 * @returns {number} Distance in pixels
 */
export const distanceToLineSegment = (point, lineStart, lineEnd) => {
  const { x: px, y: py } = point;
  const { x: x1, y: y1 } = lineStart;
  const { x: x2, y: y2 } = lineEnd;

  // Calculate line segment length squared
  const lengthSq = (x2 - x1) ** 2 + (y2 - y1) ** 2;

  // If line segment is actually a point, return distance to that point
  if (lengthSq === 0) {
    return calculateDistance(point, lineStart);
  }

  // Calculate projection parameter t (where point projects onto line)
  // t = 0 means projection is at lineStart
  // t = 1 means projection is at lineEnd
  // 0 < t < 1 means projection is between lineStart and lineEnd
  let t = ((px - x1) * (x2 - x1) + (py - y1) * (y2 - y1)) / lengthSq;

  // Clamp t to [0, 1] to stay within line segment
  t = Math.max(0, Math.min(1, t));

  // Calculate closest point on line segment
  const closestX = x1 + t * (x2 - x1);
  const closestY = y1 + t * (y2 - y1);

  // Return distance to closest point
  return calculateDistance(point, { x: closestX, y: closestY });
};

/**
 * Check if a point is inside a polygon using ray-casting algorithm
 * Also accepts points within tolerance distance of polygon edges
 *
 * @param {object} point - Point to check {x, y}
 * @param {Array<{x: number, y: number}>} polygon - Polygon vertices
 * @param {number} tolerance - Distance tolerance for edge points (default: 15px)
 * @returns {boolean} True if point is inside polygon or near edges
 */
export const isPointInPolygon = (point, polygon, tolerance = 15) => {
  if (!polygon || polygon.length < 3) return true; // No constraint if no valid polygon

  // First, check if point is strictly inside using ray-casting
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersect =
      (yi > point.y) !== (yj > point.y) &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }

  // If strictly inside, return true
  if (inside) return true;

  // If not inside, check if point is within tolerance of any edge
  for (let i = 0; i < polygon.length; i++) {
    const j = (i + 1) % polygon.length;
    const distance = distanceToLineSegment(point, polygon[i], polygon[j]);
    if (distance <= tolerance) {
      return true; // Point is near an edge
    }
  }

  // Point is outside and not near any edge
  return false;
};

/**
 * Add a new vertex to the polygon
 *
 * @param {Array} vertices - Existing vertices [{x, y}, ...]
 * @param {object} point - New point to add {x, y}
 * @param {boolean} ctrlPressed - Disable snap
 * @param {number} gridSize - Grid size in pixels
 * @param {boolean} snapEnabled - Whether snap is enabled
 * @returns {object} Result {vertices, shouldClose, isDuplicate}
 */
export const addVertex = (
  vertices,
  point,
  ctrlPressed,
  gridSize,
  snapEnabled
) => {
  // Apply snap to grid
  const x = snapValue(point.x, gridSize, snapEnabled, ctrlPressed);
  const y = snapValue(point.y, gridSize, snapEnabled, ctrlPressed);
  const snappedPoint = { x, y };

  // Check if this is near the first vertex (auto-close)
  if (vertices.length >= 3 && isNearPoint(snappedPoint, vertices[0], 10)) {
    return {
      vertices: [...vertices], // Don't add duplicate of first point
      shouldClose: true,
      isDuplicate: false,
    };
  }

  // Check if this is a duplicate of the last vertex (prevent)
  if (
    vertices.length > 0 &&
    isNearPoint(snappedPoint, vertices[vertices.length - 1], 5)
  ) {
    return {
      vertices: [...vertices],
      shouldClose: false,
      isDuplicate: true,
    };
  }

  // Add new vertex
  return {
    vertices: [...vertices, snappedPoint],
    shouldClose: false,
    isDuplicate: false,
  };
};

/**
 * Remove the last vertex (undo last point)
 *
 * @param {Array} vertices - Existing vertices [{x, y}, ...]
 * @returns {Array} Updated vertices array
 */
export const removeLastVertex = (vertices) => {
  if (vertices.length === 0) return vertices;
  return vertices.slice(0, -1);
};

/**
 * Create a new polygon with default styling
 *
 * @param {Array} vertices - Polygon vertices [{x, y}, ...]
 * @param {boolean} isActive - Whether polygon is selected/active
 * @returns {Polygon} Fabric.js Polygon object
 */
export const createPolygon = (vertices, isActive = true, objectTypeId = null) => {
  if (!vertices || vertices.length < 3) {
    throw new Error('Polygon must have at least 3 vertices');
  }

  // Get default style from config if objectTypeId provided
  let fillColor, strokeColor, strokeWidth = 2, opacity = 1;
  let objectConfig = null;

  if (objectTypeId) {
    objectConfig = getObjectTypeById(objectTypeId);
    if (objectConfig && objectConfig.defaultStyle) {
      const style = objectConfig.defaultStyle;
      fillColor = style.fill;
      strokeColor = style.stroke;
      strokeWidth = style.strokeWidth || 2;
      opacity = style.opacity !== undefined ? style.opacity : 1;
    }
  }

  // Fallback to neutral colors if no config found (for Zeichnen tab usage)
  if (!fillColor) {
    fillColor = 'rgba(107, 114, 128, 0.2)'; // Gray-500 with 20% opacity (neutral)
  }

  if (!strokeColor) {
    strokeColor = '#6B7280'; // Gray-500 (neutral)
  }

  // Calculate bounding box to maintain precise positioning
  const xs = vertices.map(v => v.x);
  const ys = vertices.map(v => v.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);

  // Convert vertices to relative coordinates (relative to top-left of bounding box)
  const relativeVertices = vertices.map(v => ({
    x: v.x - minX,
    y: v.y - minY
  }));

  const polygon = new Polygon(relativeVertices, {
    left: minX,
    top: minY,
    originX: 'left',
    originY: 'top',
    fill: fillColor,
    stroke: strokeColor,
    strokeWidth: strokeWidth,
    opacity: opacity,
    strokeUniform: true, // Maintain stroke width during scaling
    strokeLineJoin: 'round', // Round corners for better appearance
    objectCaching: false,
    perPixelTargetFind: false, // Bounding-box detection (Fabric.js v6 has correct polygon AABB)
    lockUniScaling: false, // Allow non-uniform scaling
  });

  // Apply garden object type system (won't override colors now due to fix)
  createGardenObject(GARDEN_OBJECT_TYPES.POLYGON, polygon, {
    data: {
      shapeType: 'polygon', // Renamed from 'type' to avoid conflict with garden object properties
      isActive,
      objectTypeId: objectTypeId,
      gardenObjectType: objectTypeId, // Add both for compatibility
      createdAt: new Date().toISOString(),
      vertexCount: vertices.length,
      // Store original colors for selection/deselection
      originalFill: fillColor,
      originalStroke: strokeColor,
      originalStrokeWidth: strokeWidth,
      originalOpacity: opacity,
      // Add config metadata if available
      ...(objectConfig && {
        isPlantable: objectConfig.isPlantable,
        castsShadow: objectConfig.castsShadow,
      }),
    },
  });

  return polygon;
};

/**
 * Update polygon appearance based on selection state
 *
 * @param {Polygon} polygon - Fabric.js Polygon object
 * @param {boolean} isActive - Whether polygon is selected/active
 */
export const updatePolygonAppearance = (polygon, isActive) => {
  if (!polygon || !polygon.data) return;

  if (isActive) {
    // Selected: Use emerald-500 green
    polygon.set({
      fill: 'rgba(16, 185, 129, 0.2)',
      stroke: '#10B981',
    });
  } else {
    // Deselected: Restore original config colors from object type definition
    const objectTypeId = polygon.data.objectTypeId || polygon.data.gardenObjectType;

    if (objectTypeId) {
      // Get config for this object type
      const objectConfig = getObjectTypeById(objectTypeId);

      if (objectConfig && objectConfig.defaultStyle) {
        // Restore colors from config
        polygon.set({
          fill: objectConfig.defaultStyle.fill || 'rgba(107, 114, 128, 0.2)',
          stroke: objectConfig.defaultStyle.stroke || '#6B7280',
          strokeWidth: objectConfig.defaultStyle.strokeWidth || 2,
          opacity: objectConfig.defaultStyle.opacity !== undefined ? objectConfig.defaultStyle.opacity : 1,
        });
      } else {
        // Fallback to neutral colors if no config found
        polygon.set({
          fill: 'rgba(107, 114, 128, 0.2)',
          stroke: '#6B7280',
          strokeWidth: 2,
          opacity: 1,
        });
      }
    } else {
      // No object type (legacy or Zeichnen tab usage) - use neutral colors
      polygon.set({
        fill: 'rgba(107, 114, 128, 0.2)',
        stroke: '#6B7280',
        strokeWidth: 2,
        opacity: 1,
      });
    }
  }

  // Update data
  if (polygon.data) {
    polygon.data.isActive = isActive;
  }
};

/**
 * Calculate polygon area using Shoelace formula
 *
 * @param {Array} vertices - Polygon vertices [{x, y}, ...]
 * @returns {number} Area in square pixels
 */
export const calculatePolygonArea = (vertices) => {
  if (!vertices || vertices.length < 3) return 0;

  let area = 0;
  const n = vertices.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += vertices[i].x * vertices[j].y;
    area -= vertices[j].x * vertices[i].y;
  }

  return Math.abs(area / 2);
};

/**
 * Calculate polygon centroid (center of mass)
 *
 * @param {Array} vertices - Polygon vertices [{x, y}, ...]
 * @returns {object} Centroid point {x, y}
 */
export const calculatePolygonCentroid = (vertices) => {
  if (!vertices || vertices.length === 0) {
    return { x: 0, y: 0 };
  }

  let cx = 0;
  let cy = 0;

  for (const vertex of vertices) {
    cx += vertex.x;
    cy += vertex.y;
  }

  return {
    x: cx / vertices.length,
    y: cy / vertices.length,
  };
};

/**
 * Get polygon dimensions and properties
 *
 * @param {Polygon} polygon - Fabric.js Polygon object
 * @param {number} pixelsPerCm - Conversion factor (default: 1 pixel = 1 cm)
 * @returns {object} Properties {vertices, vertexCount, areaSqm, centroid}
 */
export const getPolygonProperties = (polygon, pixelsPerCm = 1) => {
  // Get vertices from polygon
  const vertices = polygon.points || [];

  // Calculate area
  const areaPixels = calculatePolygonArea(vertices);
  const areaCm2 = areaPixels / (pixelsPerCm * pixelsPerCm);
  const areaSqm = areaCm2 / 10000; // Convert cm² to m²

  // Calculate centroid
  const centroid = calculatePolygonCentroid(vertices);
  const centroidCm = {
    x: Math.round((centroid.x / pixelsPerCm) * 100) / 100,
    y: Math.round((centroid.y / pixelsPerCm) * 100) / 100,
  };

  return {
    vertices: vertices.map((v) => ({
      x: Math.round((v.x / pixelsPerCm) * 100) / 100,
      y: Math.round((v.y / pixelsPerCm) * 100) / 100,
    })),
    vertexCount: vertices.length,
    areaSqm: Math.round(areaSqm * 100) / 100,
    centroid: centroidCm,
  };
};

/**
 * Update polygon vertices (for properties panel editing)
 *
 * @param {Polygon} polygon - Fabric.js Polygon object
 * @param {Array} vertices - New vertices [{x, y}, ...] in cm
 * @param {number} pixelsPerCm - Conversion factor
 */
export const updatePolygonVertices = (polygon, vertices, pixelsPerCm = 1) => {
  // Convert cm to pixels
  const verticesPixels = vertices.map((v) => ({
    x: v.x * pixelsPerCm,
    y: v.y * pixelsPerCm,
  }));

  polygon.set({
    points: verticesPixels,
  });
};

/**
 * Check if polygon is valid (closed, no self-intersection)
 *
 * @param {Array} vertices - Polygon vertices [{x, y}, ...]
 * @returns {object} Validation result {isValid, errors}
 */
export const validatePolygon = (vertices) => {
  const errors = [];

  // Minimum 3 vertices
  if (!vertices || vertices.length < 3) {
    errors.push('Polygon must have at least 3 vertices');
  }

  // Check for duplicate vertices
  if (vertices && vertices.length > 0) {
    for (let i = 0; i < vertices.length; i++) {
      for (let j = i + 1; j < vertices.length; j++) {
        if (isNearPoint(vertices[i], vertices[j], 1)) {
          errors.push(`Duplicate vertex detected at index ${i} and ${j}`);
        }
      }
    }
  }

  // Optional: Check for self-intersection (complex, skip for MVP)
  // Could be added later for enhanced validation

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Get perimeter of polygon
 *
 * @param {Array} vertices - Polygon vertices [{x, y}, ...]
 * @returns {number} Perimeter in pixels
 */
export const calculatePolygonPerimeter = (vertices) => {
  if (!vertices || vertices.length < 2) return 0;

  let perimeter = 0;
  const n = vertices.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    perimeter += calculateDistance(vertices[i], vertices[j]);
  }

  return perimeter;
};

/**
 * Check if two line segments intersect
 * Uses cross-product method to detect intersection
 *
 * @param {object} p1 - First line segment start {x, y}
 * @param {object} p2 - First line segment end {x, y}
 * @param {object} p3 - Second line segment start {x, y}
 * @param {object} p4 - Second line segment end {x, y}
 * @returns {boolean} True if segments intersect
 */
export const doLineSegmentsIntersect = (p1, p2, p3, p4) => {
  const { x: x1, y: y1 } = p1;
  const { x: x2, y: y2 } = p2;
  const { x: x3, y: y3 } = p3;
  const { x: x4, y: y4 } = p4;

  // Calculate cross products
  const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

  // Lines are parallel if denom is 0
  if (denom === 0) {
    return false;
  }

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;

  // Check if intersection point is within both segments
  // ua and ub must be between 0 and 1
  return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
};

/**
 * Check if polygon has self-intersecting edges
 *
 * @param {Array} vertices - Polygon vertices [{x, y}, ...]
 * @param {boolean} [isClosed=true] - When true, also considers the closing edge
 *   (last vertex -> first vertex). Pass false while a polygon is still being
 *   drawn (an open polyline), otherwise the not-yet-existing closing edge is
 *   validated and can produce false-positive "edges cross" errors on concave
 *   or zig-zag shapes.
 * @returns {boolean} True if polygon has self-intersecting edges
 */
export const hasSelfIntersection = (vertices, isClosed = true) => {
  if (!vertices || vertices.length < 4) {
    return false; // Need at least 4 vertices to have intersection
  }

  const n = vertices.length;
  // Closed polygon has n edges (incl. last->first); open polyline has n-1.
  const edgeCount = isClosed ? n : n - 1;

  // Check each edge against all other non-adjacent edges
  for (let i = 0; i < edgeCount; i++) {
    const j = (i + 1) % n;
    const edge1Start = vertices[i];
    const edge1End = vertices[j];

    // Check against all non-adjacent edges
    // Skip adjacent edges (i-1, i, i+1)
    for (let k = i + 2; k < edgeCount; k++) {
      // For a closed polygon, edge 0 and the last edge (n-1 -> 0) are adjacent
      // because they share vertex 0, so skip that pair.
      if (isClosed && i === 0 && k === n - 1) continue;

      const l = (k + 1) % n;
      const edge2Start = vertices[k];
      const edge2End = vertices[l];

      if (doLineSegmentsIntersect(edge1Start, edge1End, edge2Start, edge2End)) {
        return true;
      }
    }
  }

  return false;
};

// getObjectEndpoints, snapToNearestEndpoint, and applyHierarchicalSnap
// are now imported from snapUtils.js (see imports at top of file)
// They are re-exported above for backward compatibility
